import { ASSISTANT_PLAN_PROSE_CLASS } from "@/components/assistant/assistantPlanProse";
import { AssistantMarkdownContent } from "@/components/chat/AssistantMarkdownContent";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useSelfControlledFieldValue } from "@/components/assistant/useSelfControlledFieldValue";
import { Code2, Eye } from "lucide-react";
import { useState } from "react";

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minRows?: number;
};

export function AssistantMarkdownEditPreview({
  label,
  value,
  onChange,
  disabled = false,
  minRows = 8,
}: Props) {
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [localValue, setLocalValue] = useSelfControlledFieldValue(value, onChange);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
          {label}
        </p>
        <div className="flex rounded-lg border border-[rgba(15,23,42,0.09)] p-0.5 dark:border-white/10">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setMode("edit")}
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors",
              mode === "edit"
                ? "bg-primary text-primary-foreground"
                : "text-[#576274] hover:bg-[var(--lm-hover)] dark:text-gray-400 dark:hover:bg-white/10"
            )}
          >
            <Code2 className="h-3 w-3" />
            Edit
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setMode("preview")}
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors",
              mode === "preview"
                ? "bg-primary text-primary-foreground"
                : "text-[#576274] hover:bg-[var(--lm-hover)] dark:text-gray-400 dark:hover:bg-white/10"
            )}
          >
            <Eye className="h-3 w-3" />
            Preview
          </button>
        </div>
      </div>

      {mode === "edit" ? (
        <Textarea
          value={localValue}
          disabled={disabled}
          rows={minRows}
          onChange={(e) => setLocalValue(e.target.value)}
          className="min-h-[160px] font-mono text-sm leading-relaxed"
          placeholder="Guidelines to inject into the review prompt (optional if you add reference PDFs below)"
        />
      ) : (
        <div className="min-h-[160px] rounded-xl border border-[rgba(15,23,42,0.09)] bg-white/60 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
          {localValue.trim() ? (
            <AssistantMarkdownContent content={localValue} className={ASSISTANT_PLAN_PROSE_CLASS} />
          ) : (
            <p className="text-sm text-[#576274] dark:text-gray-400">Nothing to preview yet.</p>
          )}
        </div>
      )}

      <p className="text-xs text-[#576274] dark:text-gray-400">
        Supports Markdown (&apos;# Headers&apos;, &apos;**bold**&apos;, &apos;- lists&apos;,
        &apos;`code`&apos;)
      </p>
    </div>
  );
}
